import { Ban, CalendarCheck, CheckCircle2, Clock, FileText, GraduationCap, PlayCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import type { MenuItem } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { Skeleton } from '../../../../components/Skeleton';
import { LifecycleTimeline } from '../../../../components/LifecycleTimeline';
import { ProfileBanner, type ProfileBannerTone } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText, type StatusTone } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { addressPathLabel } from '../../../../lib/addressApi';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { MySurveyCompleteTrainingModal } from '../components/MySurveyCompleteTrainingModal';
import { MySurveyRecommendationDecisionModal } from '../components/MySurveyRecommendationDecisionModal';
import { myTrainingRecommendationLifecycle } from '../utils/myTrainingRecommendationLifecycle';
import { MySurveyEnrollModal } from '../components/MySurveyEnrollModal';
import { MySurveyTrainingModal } from '../components/MySurveyTrainingModal';
import { MySurveyTrainingReportCard } from '../components/MySurveyTrainingReportCard';
import { MySurveyTrainingReportModal } from '../components/MySurveyTrainingReportModal';
import { myTrainingRecommendationsApi } from '../../training-recommendations/myTrainingRecommendationsApi';
import { mySurveysApi } from '../mySurveysApi';
import { TRAINING_DECISION, TRAINING_RESULT, TRAINING_RESULT_LABELS, myTrainingStatus, type MySurveyTrainingRecommendation } from '../mySurveyTypes';

type Open = 'accept' | 'decline' | 'enroll' | 'training' | 'complete' | 'report';

type TabKey = 'info' | 'report' | 'cycle';

/** What the page needs of the survey a recommendation came from — whichever read supplied it. */
interface SourceSurvey {
  id: string;
  title: string;
  isActive: boolean;
}

const DECISION_LABELS = { 0: 'Pending', 1: 'Accepted', 2: 'Declined' } as const;

/**
 * ONE training recommendation sent to the employee — its own PROFILE (product call, 13 Oct 2026),
 * opened from Preview › Training Recommendations' table.
 *
 * On the **header** layout with two tabs. **Info** — what was recommended, and the employee's track
 * on it: answer it (accept, or decline with a reason) → start the training → complete it, on or
 * after its end date. **Report** — the completed training's report: submitted, read and edited
 * HERE (product call, 13 Oct 2026 — it moved from the survey's Preview, whose Report sub-tab is
 * gone). The ONE next step is the banner's, Submit report included; every act the state allows is
 * also behind a card's cog, each on its own key and DISABLED for state with the reason.
 *
 * The survey is read too — for the breadcrumb, and because a DELETED survey makes the track read
 * only: nothing is offered, and the server refuses each act anyway.
 *
 * **TWO MODULES OPEN IT** — Survey, from its Preview's table, and Self Service › Training
 * Recommendations, whose register spans every survey (`from`, the arrangement Learning &
 * Development's own recommendation profile takes). The register's route names no survey and its
 * reader may hold only `my-training-recommendations.read`, so there ONE read on that key brings the
 * recommendation and the survey it came from; the breadcrumb leads back to whichever list opened
 * it. Every ACT is the same form on the same Survey key either way.
 */
export function MySurveyTrainingRecommendationProfilePage({ from = 'survey' }: { from?: 'survey' | 'training-recommendations' }) {
  const { id: routeSurveyId, recommendationId } = useParams<{ id: string; recommendationId: string }>();
  const navigate = useNavigate();
  const fromRegister = from === 'training-recommendations';

  const [survey, setSurvey] = useState<SourceSurvey | null>(null);
  const [recommendation, setRecommendation] = useState<MySurveyTrainingRecommendation | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  // ONE piece of state for which form is open — never five booleans.
  const [open, setOpen] = useState<Open | null>(null);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!recommendationId || (!fromRegister && !routeSurveyId)) return;

      const read: Promise<[SourceSurvey, MySurveyTrainingRecommendation]> = fromRegister
        ? myTrainingRecommendationsApi
            .get(recommendationId, signal)
            .then((detail) => [{ id: detail.surveyId, title: detail.surveyTitle, isActive: detail.isActive }, detail.recommendation])
        : Promise.all([
            mySurveysApi.get(routeSurveyId!, signal),
            mySurveysApi.trainingRecommendation(routeSurveyId!, recommendationId, signal),
          ]).then(([nextSurvey, nextRecommendation]) => [
            { id: nextSurvey.id, title: nextSurvey.title, isActive: nextSurvey.isActive },
            nextRecommendation,
          ]);

      read
        .then(([nextSurvey, nextRecommendation]) => {
          if (signal?.aborted) return;

          setSurvey(nextSurvey);
          setRecommendation(nextRecommendation);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this training recommendation.', 0));
        });
    },
    [fromRegister, routeSurveyId, recommendationId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // The breadcrumb is the back-nav: the list that opened the page is one segment up — the
  // survey's own profile, or the register.
  const breadcrumb: BreadcrumbSegment[] = fromRegister
    ? [
        'Self Service',
        'Learning & Development',
        { label: 'Training Recommendations', to: '/self-service/training-recommendations' },
        recommendation ? recommendation.title : 'Training recommendation',
      ]
    : [
        'Self Service',
        'Learning & Development',
        { label: 'Survey', to: '/self-service/surveys' },
        { label: survey ? survey.title : 'Survey', to: `/self-service/surveys/${routeSurveyId ?? ''}` },
        recommendation ? recommendation.title : 'Training recommendation',
      ];

  // A failed REFRESH keeps the page; only a first load that failed replaces it.
  if (error && !recommendation) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!recommendation || !survey) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  // The survey every act is routed through — the route's, or the one the register's read named.
  const surveyId = survey.id;
  const { id, decision } = recommendation;
  // `?? null`: off the wire an absent training is undefined.
  const training = recommendation.training ?? null;
  const enrollment = recommendation.enrollment ?? null;
  // A training started before the Enroll step existed counts as enrolled: its dates are in.
  const enrolled = enrollment !== null || training !== null;
  const startsOn = training?.startDate ?? enrollment?.startDate;
  const endsOn = training?.endDate ?? enrollment?.endDate;
  const readOnly = !survey.isActive;
  const accepted = decision === TRAINING_DECISION.Accepted;
  // ENDED either way; the result says which. An end with no result predates the question — a completion.
  const ended = training?.completedAt != null;
  const cancelled = ended && training?.result === TRAINING_RESULT.Cancelled;
  // COMPLETED is what a report is written on — never a cancelled training.
  const completed = ended && !cancelled;
  const reported = training?.reportAt != null;

  const done = () => {
    setOpen(null);
    load();
  };

  const state = bannerFor(recommendation, readOnly);

  // Every act the track knows, each on its own key; DISABLED for state with the reason, never hidden.
  const menuItems: MenuItem[] = readOnly
    ? []
    : [
        {
          key: 'enroll',
          label: enrollment ? 'Edit enrollment' : 'Enroll',
          permission: 'my-surveys.training',
          disabledReason: !accepted
            ? 'Accept this training recommendation before enrolling in its training.'
            : ended
              ? 'This training has ended, so it can no longer be changed.'
              : null,
          onClick: () => setOpen('enroll'),
        },
        {
          key: 'training',
          label: training ? 'Edit training' : 'Start training',
          permission: 'my-surveys.training',
          disabledReason: !accepted
            ? 'Accept this training recommendation before starting its training.'
            : !enrolled
              ? 'Enroll in this training before starting it.'
            : ended
              ? 'This training has ended, so it can no longer be changed.'
              : null,
          onClick: () => setOpen('training'),
        },
        {
          key: 'complete',
          label: 'End training',
          permission: 'my-surveys.training',
          disabledReason: !training
            ? 'Start your training before ending it.'
            : ended
              ? 'This training has already ended.'
              : null,
          onClick: () => setOpen('complete'),
        },
      ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="my-training-recommendation-profile"
        header={
          <ProfileHeader
            id="my-training-recommendation-profile-header"
            icon={<GraduationCap className="h-6 w-6" aria-hidden="true" />}
            title={recommendation.title}
            // The survey it came from — one fact, the breadcrumb's own.
            meta={[survey.title]}
            status={<StatusText tone={toneFor(recommendation)}>{myTrainingStatus({ ...recommendation, enrolledAt: enrollment?.enrolledAt, trainingStartDate: training?.startDate, trainingCompletedAt: training?.completedAt, trainingResult: training?.result })}</StatusText>}
            // The STANDARD header: the status, and under it who created the record and when.
            createdBy={recommendation.createdByName}
            createdAt={recommendation.createdAt}
          />
        }
        banner={
          <ProfileBanner
            id="my-training-recommendation-profile-banner"
            tone={state.tone}
            icon={state.icon}
            message={state.message}
            // The ACTION is gated; the sentence is for every reader. Each only opens a form, whose own
            // tick is the confirmation.
            action={
              readOnly ? undefined : decision === TRAINING_DECISION.Pending ? (
                <PermissionGate permission="my-surveys.decide-plan">
                  <div className="flex items-center gap-2">
                    <Button id="my-training-recommendation-decline" variant="secondary" onClick={() => setOpen('decline')}>
                      Decline
                    </Button>
                    <Button id="my-training-recommendation-accept" variant="primary" onClick={() => setOpen('accept')}>
                      Accept
                    </Button>
                  </div>
                </PermissionGate>
              ) : accepted && !ended ? (
                /*
                  ONE button, and it CHANGES (product call, 13 Oct 2026): Enroll — the school, its
                  location and the duration — then Start training, then End training for as long as
                  the training is under way (it no longer waits for the end date). Start training is
                  not shown again to edit; editing is the Training card's menu.
                */
                <PermissionGate permission="my-surveys.training">
                  {training ? (
                    <Button
                      id="my-training-recommendation-complete"
                      variant="primary"
                      leading={<CalendarCheck className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setOpen('complete')}
                    >
                      End training
                    </Button>
                  ) : !enrolled ? (
                    <Button
                      id="my-training-recommendation-enroll"
                      variant="primary"
                      leading={<GraduationCap className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setOpen('enroll')}
                    >
                      Enroll
                    </Button>
                  ) : (
                    <Button
                      id="my-training-recommendation-start"
                      variant="primary"
                      leading={<PlayCircle className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setOpen('training')}
                    >
                      Start training
                    </Button>
                  )}
                </PermissionGate>
              ) : completed && !reported ? (
                // COMPLETED, no report in: what comes next is the report — this profile's own.
                <PermissionGate permission="my-surveys.training">
                  <Button
                    id="my-training-recommendation-report"
                    variant="primary"
                    leading={<FileText className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setOpen('report')}
                  >
                    Submit report
                  </Button>
                </PermissionGate>
              ) : undefined
            }
          />
        }
        panel="cards"
        tabs={[
          { key: 'info', label: 'Info' },
          // CLOSED until the training is ended (product call, 13 Oct 2026): a report is written on a
          // completed training, so before that there is nothing behind the tab. Disabled for STATE.
          // A CANCELLED training takes no report, so the tab stays closed for it too.
          { key: 'report', label: 'Report', disabled: !completed },
          // The track's own steps, each with its date (product call, 13 Oct 2026).
          { key: 'cycle', label: 'Life cycle' },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {tab === 'cycle' ? (
          // No cog: nothing on a life cycle is edited — the Audit card's exemption.
          <ProfileCard id="my-training-recommendation-profile-cycle" title="Life cycle" padded>
            <LifecycleTimeline
              id="my-training-recommendation-lifecycle"
              events={myTrainingRecommendationLifecycle(recommendation)}
              // The card already names it.
              title={null}
              empty="Nothing has happened yet."
            />
          </ProfileCard>
        ) : tab === 'report' && completed && training ? (
          // The SAME card every training's report reads in. Edit behind its cog; Submit in its empty state.
          <MySurveyTrainingReportCard
            id="my-training-recommendation-profile-report"
            training={training}
            onEdit={readOnly ? undefined : () => setOpen('report')}
            fetchAttachment={(attachmentId) => mySurveysApi.downloadRecommendationReportAttachment(surveyId, id, attachmentId)}
          />
        ) : (
          <>
            {/* Learning & Development's record — what was recommended. Nothing here is the employee's to edit. */}
            <ProfileCard
              id="my-training-recommendation-profile-recommendation"
              title="Recommendation"
              padded
              // From the register the breadcrumb does not pass through the survey, so the card links to
              // it — where the report is. Another module's page, so the entry carries that module's key.
              menuItems={
                fromRegister
                  ? [
                      {
                        key: 'open-survey',
                        label: 'Open survey',
                        permission: 'my-surveys.read',
                        onClick: () => navigate(`/self-service/surveys/${surveyId}`),
                      },
                    ]
                  : undefined
              }
            >
              {/* `!= null`: off the wire an absent value is undefined. The one renderer of stored markup. */}
              {recommendation.details != null && recommendation.details !== '' ? (
                <RichTextDocument html={recommendation.details} />
              ) : (
                <p className="text-sm text-slate-400">—</p>
              )}
            </ProfileCard>

            {/* The employee's own track. Moved by ACTIONS — behind the cog — never by typing into the card. */}
            <ProfileCard id="my-training-recommendation-profile-training" title="Training" menuItems={menuItems}>
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Sent', value: <Instant value={recommendation.sentAt} /> },
                  { label: 'Status', value: DECISION_LABELS[decision] },
                  { label: 'Date responded', value: recommendation.decidedAt != null ? <Instant value={recommendation.decidedAt} /> : null },
                  ...(decision === TRAINING_DECISION.Declined ? [{ label: 'Your reason', value: recommendation.declineReason ?? null }] : []),
                  { label: 'Enrolled', value: enrollment?.enrolledAt != null ? <Instant value={enrollment.enrolledAt} /> : null },
                  { label: 'Training school', value: enrollment?.school ?? null },
                  // `|| null`: no location recorded is an em dash, not an empty string.
                  { label: 'Location', value: (enrollment ? addressPathLabel(enrollment) : '') || null },
                  { label: 'Street', value: enrollment?.streetAddress ?? null },
                  // Calendar dates — `formatDateOnly`, never the instant path.
                  // Once started, the dates Start training recorded; until then, the duration given at enrollment.
                  { label: 'Training starts', value: startsOn != null ? formatDateOnly(startsOn) : null, mono: true },
                  { label: 'Training ends', value: endsOn != null ? formatDateOnly(endsOn) : null, mono: true },
                  ...(training?.remarks ? [{ label: 'Why it was changed', value: training.remarks }] : []),
                  // How it ended, when, and why — the End training form's own three answers.
                  { label: 'Result', value: ended ? TRAINING_RESULT_LABELS[cancelled ? TRAINING_RESULT.Cancelled : TRAINING_RESULT.Completed] : null },
                  { label: 'Ended', value: training?.completedAt != null ? <Instant value={training.completedAt} /> : null },
                  { label: 'Reason', value: training?.completionRemarks ?? null },
                  // A fact only — the report itself is this profile's Report tab.
                  { label: 'Report submitted', value: reported && training?.reportAt != null ? <Instant value={training.reportAt} /> : null },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {open === 'report' && training ? (
        <MySurveyTrainingReportModal
          surveyId={surveyId}
          recommendationId={id}
          training={training}
          onClose={() => setOpen(null)}
          onSaved={() => {
            // Land on what was just written.
            setTab('report');
            done();
          }}
        />
      ) : null}

      {open === 'accept' || open === 'decline' ? (
        <MySurveyRecommendationDecisionModal
          surveyId={surveyId}
          recommendationId={id}
          title={recommendation.title}
          accept={open === 'accept'}
          onClose={() => setOpen(null)}
          onDecided={done}
        />
      ) : null}

      {open === 'enroll' ? (
        <MySurveyEnrollModal
          surveyId={surveyId}
          recommendationId={id}
          recommendationTitle={recommendation.title}
          enrollment={enrollment}
          onClose={() => setOpen(null)}
          onSaved={done}
        />
      ) : null}

      {open === 'training' ? (
        <MySurveyTrainingModal
          surveyId={surveyId}
          recommendationId={id}
          recommendationTitle={recommendation.title}
          enrolledEndDate={enrollment?.endDate}
          training={training}
          onClose={() => setOpen(null)}
          onSaved={done}
        />
      ) : null}

      {open === 'complete' && training ? (
        <MySurveyCompleteTrainingModal
          surveyId={surveyId}
          recommendationId={id}
          training={training}
          onClose={() => setOpen(null)}
          // Ended — and, where the employee ticked it, straight on to the report form.
          onCompleted={(proceedToReport) => {
            setOpen(proceedToReport ? 'report' : null);
            load();
          }}
        />
      ) : null}
    </div>
  );
}

/** An instant, in the machine face, inside its `<time>`. */
function Instant({ value }: { value: string }) {
  return (
    <time dateTime={toIso(value)} className="font-mono">
      {formatDateTime(value)}
    </time>
  );
}

/** The header pill's tone — a profile may carry amber; an index page may not. */
function toneFor(recommendation: MySurveyTrainingRecommendation): StatusTone {
  if (recommendation.decision === TRAINING_DECISION.Pending) return 'pending';
  if (recommendation.decision === TRAINING_DECISION.Declined) return 'blocked';

  if (recommendation.training?.completedAt == null) return 'inactive';

  return recommendation.training.result === TRAINING_RESULT.Cancelled ? 'blocked' : 'active';
}

/** The banner's sentence for every state — it renders for each, not only the ones with something to do. */
function bannerFor(
  recommendation: MySurveyTrainingRecommendation,
  readOnly: boolean,
): { tone: ProfileBannerTone; icon: React.ReactNode; message: string } {
  const icon = (Glyph: typeof Clock) => <Glyph className="h-4 w-4" aria-hidden="true" />;
  const training = recommendation.training ?? null;

  if (readOnly) {
    return { tone: 'red', icon: icon(Ban), message: 'This survey is deleted, so this training recommendation can no longer be acted on. Nothing is erased.' };
  }

  if (recommendation.decision === TRAINING_DECISION.Pending) {
    return { tone: 'amber', icon: icon(Clock), message: 'This training was recommended for you. Accept or decline it.' };
  }

  if (recommendation.decision === TRAINING_DECISION.Declined) {
    return {
      tone: 'red',
      icon: icon(Ban),
      message: `You declined this training recommendation${recommendation.decidedAt != null ? ` on ${formatDateTime(recommendation.decidedAt)}` : ''}.`,
    };
  }

  if (training == null) {
    return recommendation.enrollment == null
      ? { tone: 'slate', icon: icon(GraduationCap), message: 'You accepted this recommendation. Enroll in the training: name the training school and its location.' }
      : { tone: 'slate', icon: icon(PlayCircle), message: 'You are enrolled. Start the training: record when it starts and ends.' };
  }

  if (training.completedAt == null) {
    return {
      tone: 'slate',
      icon: icon(Clock),
      message: `Your training is under way, until ${formatDateOnly(training.endDate)}. End it once it is completed, or if it is cancelled.`,
    };
  }

  if (training.result === TRAINING_RESULT.Cancelled) {
    return { tone: 'red', icon: icon(Ban), message: `Your training was cancelled on ${formatDateTime(training.completedAt)}. A cancelled training takes no report.` };
  }

  return training.reportAt == null
    ? { tone: 'emerald', icon: icon(CheckCircle2), message: 'Your training is completed. Submit your report on it, with any certificates or documents.' }
    : { tone: 'emerald', icon: icon(CheckCircle2), message: 'Your training is completed and your report is submitted. Read or revise it under Report.' };
}
